import { useSyncExternalStore } from "react";

/**
 * Host-owned purchase-confirmation gate for the playable bridge.
 *
 * A sandboxed playable can never be allowed to spend the user's Mubux silently:
 * `economy.purchase` must surface a confirmation the game cannot forge or
 * auto-click. This store lives in the trusted host (outside the iframe) and
 * mirrors the friend-bridge pattern — the sandbox `await`s `requestPurchaseConfirm`,
 * the host renders the dialog, and `resolvePurchaseConfirm` settles the promise.
 */
export interface PurchaseConfirmRequest {
	offerId: string;
	/** Offer title (display only) from the playable spec, or null when it isn't declared. */
	title: string | null;
	/**
	 * Mubux price to display. On a new SDK this is the AUTHORITATIVE price from `economy.quote`
	 * (the charge is price-locked to it); on the legacy host-intercept fallback it's read from the
	 * playable spec. Null when it isn't a Mubux-priced offer / unknown.
	 */
	priceMubux: number | null;
	/** The playable's title, shown so the user knows who is asking to charge them. */
	playableTitle: string | null;
}

let currentRequest: PurchaseConfirmRequest | null = null;
let currentResolver: ((confirmed: boolean) => void) | null = null;
const listeners = new Set<() => void>();

function emit() {
	for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

function snapshot() {
	return currentRequest;
}

/**
 * Ask the host to confirm a purchase. Resolves `true` if the user confirms,
 * `false` if they cancel (or a newer request supersedes this one).
 */
export function requestPurchaseConfirm(request: PurchaseConfirmRequest): Promise<boolean> {
	if (currentResolver) currentResolver(false);
	currentRequest = request;
	return new Promise((resolve) => {
		currentResolver = resolve;
		emit();
	});
}

/** Settle the active confirmation and clear the dialog. No-op if nothing is pending. */
export function resolvePurchaseConfirm(confirmed: boolean) {
	const resolver = currentResolver;
	currentRequest = null;
	currentResolver = null;
	emit();
	resolver?.(confirmed);
}

/** Dismiss a still-pending confirmation as cancelled (e.g. the sandbox tore down). */
export function dismissPurchaseConfirm() {
	if (currentResolver) resolvePurchaseConfirm(false);
}

export function usePurchaseConfirm(): PurchaseConfirmRequest | null {
	return useSyncExternalStore(subscribe, snapshot, snapshot);
}
